/**
 * Button — the action control every other control borrows its metrics from.
 *
 * Geometry is pinned to the `--h-control*` tokens so a Button, an Input and a
 * Select trigger placed on the same row share an edge to the pixel. `loading`
 * never changes the measured width: when there is a left icon the spinner takes
 * its slot, otherwise the label stays in flow and is hidden while a centred
 * spinner is overlaid. A wide button with no icon should pass `loadingLabel`
 * instead — the spinner moves into the icon slot and the word is swapped rather
 * than deleted. `loading` never changes the *colour* either — see
 * `VARIANT_DISABLED`.
 */

import { cloneElement, forwardRef, isValidElement } from 'react';
import type {
  ButtonHTMLAttributes,
  ElementType,
  ReactElement,
  ReactNode,
  Ref,
  SVGProps,
} from 'react';
import { cn } from '@/lib/cn';

export type ControlSize = 'sm' | 'md' | 'lg';

/**
 * Buttons get one step the shared control scale does not: `xl`, the height a
 * control that *ends* a task takes above the controls that feed it. It is a
 * Button-only step — an Input or a Select never wears it — so it extends this
 * union rather than `ControlSize`, which every other control's size map is
 * keyed by.
 */
export type ButtonSize = ControlSize | 'xl';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'outline'
  | 'danger'
  | 'subtle';

/* ── Slot ─────────────────────────────────────────────────────────────────
   A 40-line `asChild` implementation. Merges handlers (child first, then the
   slot's), composes className/style and chains refs. No dependency. */

type AnyProps = Record<string, unknown>;

function assignRef<T>(ref: Ref<T> | undefined, value: T | null): void {
  if (typeof ref === 'function') ref(value);
  else if (ref && typeof ref === 'object') (ref as { current: T | null }).current = value;
}

/** Fan one node out to several refs. Returns a callback ref (never a cleanup). */
export function composeRefs<T>(...refs: Array<Ref<T> | undefined>) {
  return (node: T | null): void => {
    for (const ref of refs) assignRef(ref, node);
  };
}

function mergeSlotProps(slotProps: AnyProps, childProps: AnyProps): AnyProps {
  const merged: AnyProps = { ...childProps };

  for (const key of Object.keys(slotProps)) {
    const slotValue = slotProps[key];
    const childValue = childProps[key];

    if (/^on[A-Z]/.test(key)) {
      if (typeof slotValue === 'function' && typeof childValue === 'function') {
        merged[key] = (...args: unknown[]) => {
          (childValue as (...a: unknown[]) => unknown)(...args);
          (slotValue as (...a: unknown[]) => unknown)(...args);
        };
      } else if (typeof slotValue === 'function') {
        merged[key] = slotValue;
      }
    } else if (key === 'className') {
      merged.className = cn(slotValue as string | undefined, childValue as string | undefined);
    } else if (key === 'style') {
      merged.style = { ...(slotValue as object), ...(childValue as object) };
    } else if (slotValue !== undefined) {
      merged[key] = slotValue;
    }
  }

  return merged;
}

export interface SlotProps {
  children?: ReactNode;
  [key: string]: unknown;
}

/** Renders its single child, merging the props it was given into it. */
export const Slot = forwardRef<HTMLElement, SlotProps>(function Slot(
  { children, ...slotProps },
  ref,
) {
  if (!isValidElement(children)) return null;
  const element = children as ReactElement<AnyProps>;
  const childRef = (element.props as { ref?: Ref<HTMLElement> }).ref;

  return cloneElement(element, {
    ...mergeSlotProps(slotProps, element.props),
    ref: composeRefs(ref, childRef),
  } as Partial<AnyProps>);
});

/* ── Spinner ──────────────────────────────────────────────────────────────
   Inherits `currentColor` and its box size from the parent's `[&_svg]` rule so
   a loading control keeps the exact footprint of its resting state. */

export function Spinner({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      focusable="false"
      className={cn('shrink-0', className)}
      style={{ animation: 'af-spin calc(var(--dur-slow)*2) linear infinite' }}
      {...rest}
    >
      <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.8" opacity="0.22" />
      <path
        d="M14.4 8A6.4 6.4 0 0 0 8 1.6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ── Recipes ──────────────────────────────────────────────────────────────── */

const BASE = [
  'relative inline-flex shrink-0 select-none items-center justify-center',
  'rounded-sm border font-medium leading-none whitespace-nowrap',
  'transition-[background-color,border-color,color,box-shadow,opacity]',
  'duration-[var(--dur-fast)] ease-[var(--ease-out)]',
  // Icons sit on the text's optical centre, half a pixel above the box centre.
  '[&_svg]:shrink-0 [&_svg]:-translate-y-[0.5px]',
].join(' ');

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-[var(--h-control-sm)] gap-1.5 px-2 text-sm [&_svg]:size-3.5',
  md: 'h-[var(--h-control)] gap-1.5 px-2.5 text-base [&_svg]:size-3.5',
  lg: 'h-[var(--h-control-lg)] gap-2 px-3.5 text-md [&_svg]:size-4',
  xl: 'h-[var(--h-control-xl)] gap-2 px-4 text-md [&_svg]:size-4',
};

/** Icons want ~2px less breathing room on their side than a text edge does. */
const PAD_ICON_LEFT: Record<ButtonSize, string> = {
  sm: 'pl-1.5',
  md: 'pl-2',
  lg: 'pl-3',
  xl: 'pl-3.5',
};
const PAD_ICON_RIGHT: Record<ButtonSize, string> = {
  sm: 'pr-1.5',
  md: 'pr-2',
  lg: 'pr-3',
  xl: 'pr-3.5',
};

/* Disabled must beat `hover:` — stacked variants win on specificity, so the
   overrides are spelled out rather than relying on source order. */
const DISABLED_FILLED = [
  'disabled:cursor-not-allowed disabled:shadow-none',
  'disabled:bg-surface-3 disabled:hover:bg-surface-3',
  'disabled:text-muted disabled:hover:text-muted',
  'disabled:border-transparent disabled:hover:border-transparent',
  'disabled:opacity-100 disabled:hover:opacity-100',
].join(' ');

const DISABLED_QUIET = [
  'disabled:cursor-not-allowed disabled:shadow-none',
  'disabled:bg-transparent disabled:hover:bg-transparent',
  'disabled:text-muted disabled:hover:text-muted',
  'disabled:border-transparent disabled:hover:border-transparent',
  'disabled:opacity-100 disabled:hover:opacity-100',
].join(' ');

const VARIANT: Record<ButtonVariant, string> = {
  primary: cn(
    // `border-accent-edge`, not `border-transparent`. The filled button is a UI
    // component boundary and owes 3:1 against the page (WCAG 1.4.11) — a floor
    // the light theme used to buy by DARKENING the fill, which left the one
    // brand mark in the product reading gold in dark and bronze in light. The
    // boundary is bought from the edge instead: `--accent-edge` is the fill
    // itself in dark (so nothing moves) and a real step in light, and `--accent`
    // is now the same gold in both themes. See tokens.css.
    'border-accent-edge bg-accent text-on-accent shadow-1',
    'hover:bg-accent-hover active:opacity-90',
  ),
  secondary: cn(
    'border-line bg-surface-2 text-primary shadow-1',
    'hover:border-line-strong hover:bg-surface-3 active:bg-active',
  ),
  outline: cn(
    'border-line-strong bg-transparent text-secondary',
    'hover:bg-hover hover:text-primary active:bg-active',
  ),
  ghost: cn(
    'border-transparent bg-transparent text-secondary',
    'hover:border-line hover:bg-hover hover:text-primary active:bg-active',
  ),
  subtle: cn(
    'border-transparent bg-hover text-secondary',
    'hover:bg-active hover:text-primary active:bg-active',
  ),
  danger: cn(
    // `--text-inverse` is the only foreground that clears 4.5:1 on `--danger`
    // in *both* themes (#081527 on #fb7185, #ffffff on #dc2626).
    'border-transparent bg-danger text-inverse shadow-1',
    'hover:opacity-90 active:opacity-80',
  ),
};

/**
 * Disabled *styling*, applied only when the control is genuinely unavailable.
 *
 * `loading` also sets the `disabled` attribute — the request must not be fired
 * twice — but a pending control keeps its resting fill: a primary CTA that goes
 * grey the moment it is pressed reads as "gone", not as "working".
 */
const VARIANT_DISABLED: Record<ButtonVariant, string> = {
  primary: DISABLED_FILLED,
  secondary: DISABLED_FILLED,
  outline: DISABLED_QUIET,
  ghost: DISABLED_QUIET,
  subtle: DISABLED_QUIET,
  danger: DISABLED_FILLED,
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Blocks interaction and shows a spinner without changing the button's box. */
  loading?: boolean;
  /**
   * The word a working button says instead of its resting label.
   *
   * Without an `iconLeft` there is no slot for the spinner, so the label is
   * hidden and the spinner is centred over it. That is right for a 30px control
   * and wrong for the one wide button that ends a task: a 520px gold bar
   * containing nothing but a 14px spinner reads as an empty control, not as a
   * working one. Pass this and the spinner takes the left icon slot while the
   * button keeps a word — the footprint is unchanged either way.
   */
  loadingLabel?: string;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
  /** Render the child element instead of a `<button>`, merging props into it. */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    loadingLabel,
    iconLeft,
    iconRight,
    fullWidth = false,
    asChild = false,
    className,
    children,
    disabled,
    type,
    ...rest
  },
  ref,
) {
  const Comp = (asChild ? Slot : 'button') as ElementType;
  const isDisabled = disabled === true || loading;
  /** The spinner takes the left slot when there is one, or when a word replaces the label. */
  const spinnerInSlot = loading && (Boolean(iconLeft) || Boolean(loadingLabel));
  const label = loading && loadingLabel ? loadingLabel : children;
  const hasLabel = label !== undefined && label !== null && label !== false;
  /** No slot to borrow and no word to swap in — hide the label and overlay the spinner. */
  const overlaySpinner = loading && !spinnerInSlot;

  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? 'button')}
      disabled={asChild ? undefined : isDisabled}
      aria-disabled={asChild && isDisabled ? true : undefined}
      aria-busy={loading || undefined}
      data-loading={loading ? '' : undefined}
      className={cn(
        BASE,
        SIZE[size],
        VARIANT[variant],
        // A loading control stays lit; only a truly disabled one greys out.
        !loading && VARIANT_DISABLED[variant],
        loading && 'cursor-progress',
        iconLeft && hasLabel && PAD_ICON_LEFT[size],
        iconRight && hasLabel && PAD_ICON_RIGHT[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {spinnerInSlot ? <Spinner /> : iconLeft}
      {hasLabel ? (
        <span className={cn('min-w-0 truncate', overlaySpinner && 'invisible')}>{label}</span>
      ) : null}
      {iconRight}
      {overlaySpinner ? (
        <span className="absolute inset-0 grid place-items-center">
          <Spinner />
        </span>
      ) : null}
    </Comp>
  );
});
